import { useState, useRef, useEffect } from "react";
import { Phone, ChevronDown, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

// Common country codes - Italy first, then popular ones
const COUNTRY_CODES = [
  { code: "+39", country: "Italia", flag: "🇮🇹", iso: "IT" },
  { code: "+1", country: "USA/Canada", flag: "🇺🇸", iso: "US" },
  { code: "+44", country: "Regno Unito", flag: "🇬🇧", iso: "GB" },
  { code: "+33", country: "Francia", flag: "🇫🇷", iso: "FR" },
  { code: "+49", country: "Germania", flag: "🇩🇪", iso: "DE" },
  { code: "+34", country: "Spagna", flag: "🇪🇸", iso: "ES" },
  { code: "+41", country: "Svizzera", flag: "🇨🇭", iso: "CH" },
  { code: "+43", country: "Austria", flag: "🇦🇹", iso: "AT" },
  { code: "+32", country: "Belgio", flag: "🇧🇪", iso: "BE" },
  { code: "+31", country: "Paesi Bassi", flag: "🇳🇱", iso: "NL" },
  { code: "+351", country: "Portogallo", flag: "🇵🇹", iso: "PT" },
  { code: "+30", country: "Grecia", flag: "🇬🇷", iso: "GR" },
  { code: "+48", country: "Polonia", flag: "🇵🇱", iso: "PL" },
  { code: "+420", country: "Rep. Ceca", flag: "🇨🇿", iso: "CZ" },
  { code: "+36", country: "Ungheria", flag: "🇭🇺", iso: "HU" },
  { code: "+40", country: "Romania", flag: "🇷🇴", iso: "RO" },
  { code: "+385", country: "Croazia", flag: "🇭🇷", iso: "HR" },
  { code: "+386", country: "Slovenia", flag: "🇸🇮", iso: "SI" },
  { code: "+381", country: "Serbia", flag: "🇷🇸", iso: "RS" },
  { code: "+355", country: "Albania", flag: "🇦🇱", iso: "AL" },
  { code: "+7", country: "Russia", flag: "🇷🇺", iso: "RU" },
  { code: "+86", country: "Cina", flag: "🇨🇳", iso: "CN" },
  { code: "+81", country: "Giappone", flag: "🇯🇵", iso: "JP" },
  { code: "+82", country: "Corea del Sud", flag: "🇰🇷", iso: "KR" },
  { code: "+91", country: "India", flag: "🇮🇳", iso: "IN" },
  { code: "+61", country: "Australia", flag: "🇦🇺", iso: "AU" },
  { code: "+55", country: "Brasile", flag: "🇧🇷", iso: "BR" },
  { code: "+52", country: "Messico", flag: "🇲🇽", iso: "MX" },
  { code: "+54", country: "Argentina", flag: "🇦🇷", iso: "AR" },
  { code: "+971", country: "Emirati Arabi", flag: "🇦🇪", iso: "AE" },
  { code: "+966", country: "Arabia Saudita", flag: "🇸🇦", iso: "SA" },
  { code: "+90", country: "Turchia", flag: "🇹🇷", iso: "TR" },
  { code: "+972", country: "Israele", flag: "🇮🇱", iso: "IL" },
  { code: "+27", country: "Sudafrica", flag: "🇿🇦", iso: "ZA" },
  { code: "+234", country: "Nigeria", flag: "🇳🇬", iso: "NG" },
  { code: "+20", country: "Egitto", flag: "🇪🇬", iso: "EG" },
  { code: "+212", country: "Marocco", flag: "🇲🇦", iso: "MA" },
];

/**
 * PhoneInput - Phone number input with country code selector
 * 
 * Props:
 * - value: string - Full phone number with country code (e.g., "+39 3331234567")
 * - onChange: (fullNumber: string, parts: { countryCode: string, number: string }) => void
 * - placeholder: string - Input placeholder
 * - className: string - Additional classes
 * - disabled: boolean - Disable input
 * - defaultCountryCode: string - Default country code (default: "+39")
 */
export default function PhoneInput({
  value = "",
  onChange,
  placeholder = "Numero di telefono",
  className = "",
  disabled = false,
  defaultCountryCode = "+39"
}) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const wrapperRef = useRef(null);
  const searchInputRef = useRef(null);

  // Parse initial value
  const parsePhoneNumber = (val) => {
    if (!val) return { countryCode: defaultCountryCode, number: "" };
    
    // Try to extract country code
    for (const country of COUNTRY_CODES) {
      if (val.startsWith(country.code)) {
        return {
          countryCode: country.code,
          number: val.slice(country.code.length).trim()
        };
      }
    }
    
    // If starts with +, try to find matching code
    if (val.startsWith("+")) {
      const potentialCode = val.split(" ")[0];
      const match = COUNTRY_CODES.find(c => c.code === potentialCode);
      if (match) {
        return {
          countryCode: match.code,
          number: val.slice(match.code.length).trim()
        };
      }
    }
    
    return { countryCode: defaultCountryCode, number: val.replace(/^\+\d+\s*/, "") };
  };

  const { countryCode, number } = parsePhoneNumber(value);
  const [selectedCode, setSelectedCode] = useState(countryCode);
  const [phoneNumber, setPhoneNumber] = useState(number);

  // Sync with external value
  useEffect(() => {
    const parsed = parsePhoneNumber(value);
    setSelectedCode(parsed.countryCode);
    setPhoneNumber(parsed.number);
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
        setSearchQuery("");
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (showDropdown && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [showDropdown]);

  const selectedCountry = COUNTRY_CODES.find(c => c.code === selectedCode) || COUNTRY_CODES[0];

  const filteredCountries = searchQuery
    ? COUNTRY_CODES.filter(c => 
        c.country.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.code.includes(searchQuery) ||
        c.iso.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : COUNTRY_CODES;

  const handleCountrySelect = (country) => {
    setSelectedCode(country.code);
    setShowDropdown(false);
    setSearchQuery("");
    
    const fullNumber = phoneNumber ? `${country.code} ${phoneNumber}` : country.code;
    onChange?.(fullNumber, { countryCode: country.code, number: phoneNumber });
  };

  const handleNumberChange = (e) => {
    // Only allow numbers, spaces, and dashes
    const newNumber = e.target.value.replace(/[^\d\s-]/g, "");
    setPhoneNumber(newNumber);
    
    const fullNumber = newNumber ? `${selectedCode} ${newNumber}` : "";
    onChange?.(fullNumber, { countryCode: selectedCode, number: newNumber });
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="flex">
        {/* Country Code Selector */}
        <button
          type="button"
          onClick={() => !disabled && setShowDropdown(!showDropdown)}
          disabled={disabled}
          className="flex items-center gap-1.5 px-3 h-12 bg-[#F5F5F7] dark:bg-white/10 rounded-l-xl border-r border-black/5 dark:border-white/10 hover:bg-[#EBEBEB] dark:hover:bg-white/15 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="phone-country-selector"
        >
          <span className="text-lg">{selectedCountry.flag}</span>
          <span className="text-sm font-medium text-[#1D1D1F] dark:text-white min-w-[45px]">
            {selectedCode}
          </span>
          <ChevronDown size={14} className="text-[#86868B]" />
        </button>

        {/* Phone Number Input */}
        <div className="relative flex-1">
          <Input
            type="tel"
            value={phoneNumber}
            onChange={handleNumberChange}
            placeholder={placeholder}
            disabled={disabled}
            className="h-12 rounded-l-none rounded-r-xl bg-[#F5F5F7] dark:bg-white/10 border-0 pl-3"
            data-testid="phone-number-input"
          />
        </div>
      </div>

      {/* Country Dropdown */}
      {showDropdown && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-[#2D2D2F] rounded-xl shadow-lg border border-black/5 dark:border-white/10 overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-black/5 dark:border-white/10">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#86868B]" size={16} />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cerca paese..."
                className="w-full pl-9 pr-3 py-2 text-sm bg-[#F5F5F7] dark:bg-white/10 rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-[#FF6B4A]"
                data-testid="phone-country-search"
              />
            </div>
          </div>

          {/* Country List */}
          <div className="max-h-64 overflow-y-auto">
            {filteredCountries.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-[#86868B]">
                Nessun paese trovato
              </div>
            ) : (
              filteredCountries.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => handleCountrySelect(country)}
                  className={`w-full px-4 py-2.5 text-left hover:bg-[#F5F5F7] dark:hover:bg-white/5 transition-colors flex items-center gap-3 ${
                    country.code === selectedCode ? 'bg-[#FF6B4A]/10' : ''
                  }`}
                  data-testid={`phone-country-${country.iso}`}
                >
                  <span className="text-xl">{country.flag}</span>
                  <span className="flex-1 text-sm text-[#1D1D1F] dark:text-white">
                    {country.country}
                  </span>
                  <span className="text-sm font-mono text-[#86868B]">
                    {country.code}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
